import { DeleteOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { DamageNumberText } from 'components/data/helpers';
import { Button, MenuButton, Modal, useMessage } from 'components/ui';
import { DAMAGE_DELETE } from 'utils/access-control/rules';
import { useMutation } from 'utils/apollo';
import { RenderWithAccess } from 'utils/usePermissions';

import DELETE_DAMAGE from './DeleteDamage.gql';
import { DeleteDamageModal } from './DeleteDamageModal';

const StyledWarning = styled.div`
  font-size: 1.1rem;
`;

// escape can get through because it's used for a modal close by antd/rc-dialog
function keyFilter(e) {
  const { key } = e;
  if (key !== 'Escape') {
    e.stopPropagation();
  }
}

function addKeyboardListeners() {
  window.addEventListener('keydown', keyFilter, { capture: true });
  window.addEventListener('keyup', keyFilter, { capture: true });
}

function removeKeyboardListeners() {
  window.removeEventListener('keydown', keyFilter, { capture: true });
  window.removeEventListener('keyup', keyFilter, { capture: true });
}

export function DeleteDamage({ damage, onDelete = null, forceIdCheck, type }) {
  const { t } = useTranslation();
  const message = useMessage();
  const [visible, setVisible] = useState(false);
  const [disabled, setDisabled] = useState(false);

  const [deleteDamage, { loading: isDeleting }] = useMutation(DELETE_DAMAGE, {
    variables: { branchGlobalId: damage.id },
    optimisticResponse: { removeDamage: true },
  });

  useEffect(() => {
    if (visible) {
      addKeyboardListeners();
    } else {
      removeKeyboardListeners();
    }

    return function cleanup() {
      removeKeyboardListeners();
    };
  }, [visible]);

  const handleDelete = async () => {
    try {
      await deleteDamage();
      setVisible(false);
      onDelete && onDelete();
    } catch (e) {
      console.error(e);
      message.error(t('damage.deleteError', 'There was a problem deleting the Damage.'));
    }
  };

  const handleInput = ({ target }) => {
    const value = target.value.toUpperCase();
    if (value === damage.number) {
      setDisabled(false);
    }
    if (!disabled && value !== damage.number) {
      setDisabled(true);
    }
  };

  // FIXME: Update all instances of this component to remove `<RenderWithAccess .../>` wrapper
  return (
    <RenderWithAccess rules={[DAMAGE_DELETE]}>
      {type === 'button' ? (
        <Button
          id="delete-damage-button"
          title={t('damage.deleteDamage', 'Delete Damage')}
          danger
          icon={<DeleteOutlined />}
          onClick={() => setVisible(true)}
        >
          {t('common.delete', 'Delete')}
        </Button>
      ) : (
        <MenuButton
          id="delete-damage-link"
          icon={<DeleteOutlined />}
          onClick={() => setVisible(true)}
          type="link"
          danger
        >
          {t('damage.deleteDamage', 'Delete Damage')}
        </MenuButton>
      )}

      {damage && damage.tasks && damage.tasks.length ? (
        <Modal
          visible={visible}
          destroyOnClose
          onCancel={() => setVisible(false)}
          title={t('damage.unableToDelete', 'Unable to delete Damage')}
          footer={
            <Button
              data-testid="delete-damage-ok-button"
              id="delete-damage-ok-button"
              onClick={() => setVisible(false)}
            >
              {t('table.ok', 'OK')}
            </Button>
          }
        >
          <StyledWarning>
            <Trans
              i18nKey="damage.cannotDeleteHasTasks"
              defaults="This Damage cannot be deleted because it is currently associated with {{count}} {{taskWord}}. To delete this Damage first delete any Tasks that reference Damage <strong><mono>{{damageNumber}}</mono></strong>."
              values={{
                count: damage.tasks.length,
                taskWord: t('table.task', { count: damage.tasks.length }),
                damageNumber: damage.number,
              }}
              components={{ strong: <strong />, mono: <DamageNumberText /> }}
            />
          </StyledWarning>
        </Modal>
      ) : (
        <DeleteDamageModal
          forceIdCheck={forceIdCheck}
          isDeleting={isDeleting}
          disabled={disabled}
          visible={visible}
          damageNumber={damage.number}
          updateVisible={setVisible}
          onDelete={handleDelete}
          onChange={handleInput}
          onCancel={() => setVisible(false)}
        />
      )}
    </RenderWithAccess>
  );
}
